import type { IframeProps, ViewContext } from '../custom-ui/iframe/types';
import type { ThemeState } from '@atlaskit/tokens';
import type { CoreDataInner } from '@atlassian/forge-ui-types';
import {
	getResolverContext,
	getViewContext,
	getBrowserTimezone,
	getBrowserLocale,
} from '../custom-ui/iframe/utils';
import type { BridgeContext, BridgeServices, CleanupFn, RegisterCleanup } from './types';
import type { ExtensionDetails } from './types';
import { parseExtensionId } from '../utils';

/**
 * A single lazily-computed slot. Call `get(factory)` to read (computing on first access),
 * and `invalidate()` to clear so the next `get()` recomputes.
 *
 * Note: `T` must not be `null` — `null` is used as the uncomputed sentinel.
 */
class LazyCell<T extends NonNullable<unknown>> {
	private value: T | null = null;

	get(compute: () => T): T {
		if (this.value === null) {
			this.value = compute();
		}
		return this.value;
	}

	invalidate(): void {
		this.value = null;
	}
}

export type BridgeContextInput = Pick<
	IframeProps,
	| 'accountId'
	| 'bridge'
	| 'coreData'
	| 'extension'
	| 'extensionData'
	| 'extensionPayload'
	| 'extensionViewData'
	| 'locale'
	| 'timezone'
	| 'contextIds'
	| 'apolloClient'
> & {
	// surfaceColor and theme are on InnerIframeProps but not IframeProps
	surfaceColor?: string | null;
	theme?: Partial<ThemeState> | null;
};

// Registry of which BridgeContext fields emit events on change.
// Keyed on keyof BridgeContext so adding a new field will surface a type error
// if it should also have a change event but doesn't.
export const BRIDGE_CONTEXT_EVENTS = {
	viewContext: 'BRIDGE_VIEW_CONTEXT_CHANGED',
} as const satisfies Partial<Record<keyof BridgeContext, string>>;

// Maps each BridgeContextInput key to whether it changed (reference equality).
// __all__ is true if any key changed.
type Changes = Record<keyof BridgeContextInput, boolean> & { __all__: boolean };

/**
 * Tracks registered cleanup callbacks for a single BridgeContextObject instance.
 */
class CleanupRegistry {
	private readonly cleanupFns: CleanupFn[] = [];

	readonly registerCleanup: RegisterCleanup = (cleanupFn) => {
		this.cleanupFns.push(cleanupFn);
		let isDisposed = false;
		return () => {
			if (isDisposed) return;
			isDisposed = true;

			const index = this.cleanupFns.indexOf(cleanupFn);
			if (index !== -1) {
				this.cleanupFns.splice(index, 1);
			}

			cleanupFn();
		};
	};

	destroy(): void {
		const cleanupFns = [...this.cleanupFns];
		this.cleanupFns.length = 0;

		for (const cleanupFn of cleanupFns) {
			cleanupFn();
		}
	}
}

/**
 * Encapsulates the BridgeContext value, its lazy memoization, and change notifications.
 *
 * Derived fields (`resolverContext`, `viewContext`) are computed lazily on first access
 * rather than eagerly on every prop change. `update()` clears the cache so the next
 * `get()` call recomputes from the latest props.
 *
 * All memoization is done as plain reference-equality comparisons — no React hooks needed.
 * This makes the logic fully testable without a React tree.
 *
 * Usage:
 *   const obj = new BridgeContextObject(initialProps);
 *   obj.update(latestProps);  // call inside useEffect — diffs and emits events
 *   obj.get();                // call from getBridgeContext getter — always current
 */
export class BridgeContextObject {
	private props: BridgeContextInput;
	private readonly getServices: () => BridgeServices;
	private readonly resolverContextCell = new LazyCell<ReturnType<typeof getResolverContext>>();
	private readonly viewContextCell = new LazyCell<ViewContext>();
	private readonly extensionDetailsCell = new LazyCell<ExtensionDetails>();
	private readonly cleanupRegistry = new CleanupRegistry();

	constructor(props: BridgeContextInput, getServices: () => BridgeServices) {
		this.props = props;
		this.getServices = getServices;
	}

	/**
	 * Tears down all resources owned by this object. Should be called on component
	 * unmount to prevent memory leaks.
	 *
	 * Currently executes all callbacks registered through `registerCleanup`.
	 * Additional cleanup steps can be added here as new bridge
	 * features are introduced.
	 */
	destroy(): void {
		this.cleanupRegistry.destroy();
	}

	/**
	 * Diffs incoming props against stored props, clears the cache, and emits
	 * change events for updated BridgeContext fields.
	 *
	 * Must be called inside useEffect (not during render) because it emits DOM events.
	 */
	update(nextProps: BridgeContextInput): void {
		const changes = this.computeChanges(nextProps);
		this.props = nextProps;

		// TODO: review invalidation logic — currently all fields are derived from all props, so any prop change invalidates everything.
		// If we add more fields that have more specific deps, we can make this smarter to avoid unnecessary recomputation and event emission.
		if (changes.__all__) {
			this.resolverContextCell.invalidate();
			this.viewContextCell.invalidate();
			this.extensionDetailsCell.invalidate();
		}
	}

	/**
	 * Returns the current BridgeContext, computing any stale lazy fields on demand.
	 * Services are resolved at call time so they are always current — even after deps
	 * like contextIds or cloudId change — without re-constructing this object.
	 */
	get(): BridgeContext {
		return {
			resolverContext: this.resolverContext,
			viewContext: this.viewContext,
			extensionPayload: this.props.extensionPayload,
			bridgeConfig: this.props.bridge ?? {},
			contextIds: this.props.contextIds,
			extension: this.props.extension,
			extensionDetails: this.extensionDetails,
			registerCleanup: this.cleanupRegistry.registerCleanup,
			services: this.getServices(),
		};
	}

	private get resolverContext(): ReturnType<typeof getResolverContext> {
		return this.resolverContextCell.get(() =>
			getResolverContext(
				{ ...this.props.coreData, appVersion: this.props.extension.appVersion } as CoreDataInner,
				this.props.extensionData,
			),
		);
	}

	private get extensionDetails(): ExtensionDetails {
		return this.extensionDetailsCell.get(() => {
			const { extension } = this.props;
			return {
				appId: extension.appId ?? parseExtensionId(extension.id).appId,
				id: extension.id.split('/').slice(0, 3).join('/'),
				installationId: extension.installationId,
			};
		});
	}

	private get viewContext(): ViewContext {
		return this.viewContextCell.get(() => this.buildViewContext(this.props, this.resolverContext));
	}

	private computeChanges(nextProps: BridgeContextInput): Changes {
		const allKeys = new Set([...Object.keys(nextProps), ...Object.keys(this.props)]) as Set<
			keyof BridgeContextInput
		>;

		const changes = {
			__all__: false,
		} as Changes;
		for (const key of allKeys) {
			const changed = nextProps[key] !== this.props[key];
			changes[key] = changed;
			if (changed) changes.__all__ = true;
		}

		// report the change fields with console output
		if (changes.__all__) {
			const changedFields = Object.keys(changes).filter(
				(k) => k !== '__all__' && changes[k as keyof BridgeContextInput],
			);
			// eslint-disable-next-line no-console
			console.log('BridgeContext changed:', changedFields);
		}

		return changes;
	}

	private buildViewContext(
		props: BridgeContextInput,
		resolverContext: ReturnType<typeof getResolverContext>,
	): ViewContext {
		return getViewContext({
			resolverContext,
			accountId: props.accountId,
			extension: props.extension,
			timezone: props.timezone ?? getBrowserTimezone(),
			locale: props.locale ?? getBrowserLocale(),
			extensionPayload: props.extensionPayload,
			extensionViewData: props.extensionViewData,
			theme: props.theme,
			surfaceColor: props.surfaceColor,
			location: window.location.toString(),
		});
	}
}
